/**
 * The drawing colours for every 2D canvas in the editor.
 *
 * Canvas 2D cannot resolve CSS custom properties — `ctx.fillStyle =
 * 'var(--rt-ink)'` is silently ignored and the context keeps whatever colour
 * was set last, so the drawing comes out stale or invisible with nothing
 * reporting a problem (canvasColours.test.ts is the guard, and it exists
 * because that shipped once). So the canvases need concrete values, and this is
 * where they come from — derived from the same tokens the DOM reads, in one
 * place, rather than five files each inventing a palette.
 *
 * The model is the one the whole port turns on: INK ON PAPER. A line is not a
 * glowing thing on a dark stage, it is deposited pigment, and density reads as
 * darkness. Concretely that means:
 *
 *   - Structure is `ink`. Full weight for what you can move, lighter for what
 *     is fixed — a drafting convention, and the reason the wing series below is
 *     four weights of one colour rather than four hues.
 *   - The ACCENT marks what the cursor is on, and nothing else. On the old dark
 *     stage hover meant "same colour, lighter", which on paper would mean
 *     "fainter", i.e. backwards. Hover now switches hue, once, to the one hue
 *     allowed to mean "here".
 *   - Handles are paper with an ink outline — a physical control resting on the
 *     sheet, not a hole punched through it.
 */
import { designTokens, designTokensDark, withAlpha, type DesignToken } from '@roomtreatment/design';
import { themeOf } from '@/lib/theme/tokens';

/**
 * The ink model survives the dark theme unchanged, because it was never
 * "dark marks" — it was "marks in the ink token". In dark, ink is the light
 * glacier and paper is navy, so ink-at-alpha still reads as density and a
 * paper-disc handle is still the brightest surface's colour. Nothing below
 * branches on the theme; it only takes the theme's tokens.
 */
export function canvasInkFor(t: Record<DesignToken, string>) {
  return {
    /** Chart wells and any canvas that needs an opaque ground of its own. Most
     *  canvases clear to transparent instead and let the panel show through. */
    paper: t.paper,

    /** Metre grid. Barely there: it is a reference, not content. */
    grid: withAlpha(t.ink, 0.07),

    /** The drawn structure — room outline, ceiling profile, plotted curve. */
    stroke: t.ink,
    /** Structure the user cannot drag (the two anchored walls). Lighter weight,
     *  which is how a drawing says "given" rather than "adjustable". */
    strokeFixed: withAlpha(t.ink, 0.35),
    /** Under the cursor, or being dragged. The only hue on the canvas. */
    strokeActive: t.accent,

    /** Wash inside the room polygon. Enough to read as enclosed, not enough to
     *  compete with anything drawn on top of it. */
    fill: withAlpha(t.ink, 0.05),
    /** The same wash under the cursor. */
    fillActive: withAlpha(t.accent, 0.10),

    /** Drag handles: paper disc, ink rim. */
    handle: t.raised,
    handleRim: t.ink,
    /** Halo behind a hovered handle. */
    handleGlow: withAlpha(t.accent, 0.24),

    /** Dimension labels and axis ticks. */
    label: t.inkFaint,
    /** Axis titles and anything that needs to read before the numbers do. */
    labelStrong: t.inkDim,
  } as const;
}

export type CanvasInk = ReturnType<typeof canvasInkFor>;

const INK_LIGHT = canvasInkFor(designTokens);
const INK_DARK = canvasInkFor(designTokensDark);

/**
 * The ink for the canvas in hand, resolved at DRAW time from the canvas's own
 * theme. This is the migration target for every drawer: take the element you
 * were given (or `ctx.canvas`) and ask it, instead of capturing `canvasInk`
 * at import — a value captured at import is frozen in whatever theme loaded
 * the module.
 */
export function canvasInkAt(el: Element | null | undefined): CanvasInk {
  return themeOf(el) === 'dark' ? INK_DARK : INK_LIGHT;
}

/** The light ink, for drawers not yet migrated to `canvasInkAt`. */
export const canvasInk = INK_LIGHT;

// The per-wing ink/fill series that used to live here went with `roomDrawer.ts`:
// side rooms were a separate drawn entity back when a room was a rectangle plus
// N attached wings. The floor plan is one polygon now, so there is nothing left
// to tell apart by weight.
